/**
 * Purchase Service
 *
 * CRUD for purchase invoices and their line items, plus the receive
 * workflow that turns line items into inventory_batches.
 *
 * @module services/purchaseService
 */

import { supabase } from './supabase';
import { terminoParaFiltro, columnaNormalizada } from '../utils/normalizacionBusqueda';
// L10 — proyecto efectivo de la línea (línea > encabezado). Gemelo del carril
// MCP en `mcp-server/src/services/purchaseProjectTag.js`.
import { effectiveLineProjectId } from '../utils/purchaseProjectTag';

const PURCHASE_STORAGE_BUCKET = 'receipts';

/** 1-hora, igual que el resto del repo (ver CLAUDE.md, política de storage). */
export const RECEIPT_SIGN_TTL_SECONDS = 3600;

/**
 * Saca el path interno del objeto a partir de una URL de Supabase Storage.
 * Un valor que no es http(s) se asume que YA es un path.
 *
 * Existe para las filas viejas: hasta hoy `purchases.attachment_url`
 * guardaba una signed URL, y esas mueren a la hora.
 */
export const extractStoragePath = (bucket, url) => {
  if (!url) return null;
  if (!/^https?:\/\//i.test(url)) return url; // ya es un path
  const match = url.match(
    new RegExp(`/storage/v1/object/(?:sign|public|authenticated)/${bucket}/([^?]+)`)
  );
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

/**
 * Devuelve una URL VIVA para el comprobante de una compra.
 *
 * EL BUG QUE CIERRA — el mismo que #332 cerró para progress_photos:
 * `uploadInvoice` firmaba una URL a 1 hora y esa URL se persistía tal
 * cual en `purchases.attachment_url` (InvoiceUploader → PurchaseForm →
 * DB) y se pintaba como `href` crudo en PurchaseDetail. Una hora después
 * de subirla, "Ver factura" llevaba a un enlace muerto: el archivo seguía
 * en el bucket, pero la fila apuntaba a nada.
 *
 * Eso con el comprobante OBLIGATORIO de Jorge es peor que un link roto —
 * es el respaldo del gasto volviéndose inalcanzable solo, sin que nadie
 * se entere hasta que alguien lo necesita.
 *
 * La regla, igual que en progress_photos: en la base va el PATH estable;
 * la URL se firma fresca en cada lectura.
 *
 * Best-effort a propósito: si no se puede derivar un path (fila muy
 * vieja con una URL externa), se devuelve el valor guardado en vez de
 * romper la pantalla entera.
 *
 * @param {string} attachmentUrl - valor de purchases.attachment_url (path o URL legacy)
 * @returns {Promise<string|null>}
 */
export const resolveAttachmentUrl = async (attachmentUrl) => {
  if (!attachmentUrl) return null;
  const path = extractStoragePath(PURCHASE_STORAGE_BUCKET, attachmentUrl);
  if (!path) return attachmentUrl;

  const { data, error } = await supabase.storage
    .from(PURCHASE_STORAGE_BUCKET)
    .createSignedUrl(path, RECEIPT_SIGN_TTL_SECONDS);

  if (error || !data?.signedUrl) {
    console.warn('No se pudo re-firmar el comprobante, se devuelve el valor guardado:', error);
    return attachmentUrl;
  }
  return data.signedUrl;
};

/**
 * Estados válidos de `purchases.status`.
 *
 * NO HAY 'pending'. Este objeto declaraba `PENDING: 'pending'` y ese valor
 * **la base lo rechaza**: el CHECK real en producción es
 *
 *   purchases_status_check CHECK (status = ANY (ARRAY[
 *     'draft','pending_approval','approved','sent',
 *     'received','partial','invoiced','cancelled']))
 *
 * (verificado por SELECT sobre pg_constraint el 2026-07-28). 'pending' salió
 * del CHECK con el ciclo de vida de #106 y quedó vivo solo en este archivo,
 * que es de donde el formulario web lo tomaba como estado por defecto — o sea
 * que crear una compra desde la web moría contra la constraint. El carril MCP
 * ya se había movido: `mcp-server/src/tools/purchaseTools.js` crea Flow A
 * directamente en 'invoiced'. Esta es la misma deriva de carriles de #353.
 *
 * Si hace falta el estado "creada pero todavía no recibida" del flujo formal,
 * el valor es DRAFT o PENDING_APPROVAL, no 'pending'.
 */
export const PURCHASE_STATUS = {
  // Flow A (legacy invoice-first): nace facturada, ver createPurchase.
  INVOICED: 'invoiced',
  PARTIAL: 'partial',
  RECEIVED: 'received',
  CANCELLED: 'cancelled',
  // Flow B (formal PO workflow — #130/#106)
  DRAFT: 'draft',
  PENDING_APPROVAL: 'pending_approval',
  APPROVED: 'approved',
  SENT: 'sent',
};

/**
 * Estados en los que una compra TODAVÍA se puede editar (encabezado + líneas).
 *
 * Por qué esta lista y no `status === 'invoiced'`, que era lo que había:
 *  - 'draft' es el estado en el que NACE una PO de Flujo B, o sea el borrador
 *    que el usuario acaba de crear. Con la comparación anterior se abría
 *    "Editar" y el formulario entero salía deshabilitado con el cartel "esta
 *    compra ya no se puede editar" — justo sobre lo único que sí se podía.
 *  - 'invoiced' es el estado inicial de Flujo A (factura en mano).
 *  - 'pending_approval' queda AFUERA a propósito: mientras alguien la está
 *    aprobando, cambiarle los montos por debajo es peor que no poder editarla.
 *    Lo mismo approved/sent/received/partial/cancelled.
 */
export const EDITABLE_PURCHASE_STATUSES = ['draft', 'invoiced'];

export const isPurchaseEditable = (status) =>
  EDITABLE_PURCHASE_STATUSES.includes(status);

export const PURCHASE_STATUS_LABELS = {
  partial: 'Parcial',
  received: 'Recibida',
  cancelled: 'Cancelada',
  draft: 'Borrador',
  pending_approval: 'Pend. Aprobación',
  approved: 'Aprobada',
  sent: 'Enviada al Proveedor',
  invoiced: 'Facturada',
};

export const PURCHASE_STATUS_VARIANTS = {
  partial: 'warning',
  received: 'success',
  cancelled: 'danger',
  draft: 'secondary',
  pending_approval: 'warning',
  approved: 'primary',
  sent: 'info',
  invoiced: 'success',
};

/**
 * List purchases with filters and pagination.
 */
export const listPurchases = async (filters = {}) => {
  try {
    let query = supabase
      .from('purchases')
      .select(
        `
        *,
        supplier:suppliers(id, name)
      `,
        { count: 'exact' }
      );

    if (filters.status) {
      query = query.eq('status', filters.status);
    }
    if (filters.supplierId) {
      query = query.eq('supplier_id', filters.supplierId);
    }
    if (filters.dateFrom) {
      query = query.gte('invoice_date', filters.dateFrom);
    }
    if (filters.dateTo) {
      query = query.lte('invoice_date', filters.dateTo);
    }
    if (filters.search) {
      query = query.or(
        `invoice_number.ilike.%${terminoParaFiltro(filters.search)}%,`
        + `${columnaNormalizada('location')}.ilike.%${terminoParaFiltro(filters.search)}%`
      );
    }

    const page = filters.page || 0;
    const pageSize = filters.pageSize || 25;
    const from = page * pageSize;
    const to = from + pageSize - 1;

    query = query.range(from, to).order('invoice_date', { ascending: false });

    const { data, count, error } = await query;
    if (error) throw error;

    return { data, count, error: null };
  } catch (error) {
    console.error('List purchases error:', error);
    return { data: null, count: 0, error };
  }
};

/**
 * Get one purchase with its line items.
 */
export const getPurchase = async (purchaseId) => {
  try {
    const { data, error } = await supabase
      .from('purchases')
      .select(
        `
        *,
        supplier:suppliers(*),
        items:purchase_items(
          *,
          inventory:inventory(id, name, sku, unit, sale_price)
        )
      `
      )
      .eq('id', purchaseId)
      .single();

    if (error) throw error;

    // #268: purchases have no direct project FK. When a PO was generated from a
    // material request, resolve the linked project through the item chain
    // (purchase_items → material_request_items → material_requests → projects)
    // so the detail page shows the real project instead of just a raw UUID note.
    const itemIds = (data?.items || []).map((it) => it.id).filter(Boolean);
    if (itemIds.length) {
      const { data: links } = await supabase
        .from('material_request_items')
        .select('request:material_requests(project:projects(id, project_number, title))')
        .in('purchase_item_id', itemIds)
        .limit(1);
      const project = links?.[0]?.request?.project || null;
      if (project) data.project = project;
    }

    return { data, error: null };
  } catch (error) {
    console.error('Get purchase error:', error);
    return { data: null, error };
  }
};

/**
 * Create a purchase with optional line items.
 * @param {Object} purchaseData - Header fields
 * @param {Array} items - Line items: { inventory_id, quantity_ordered, unit_cost, lot_number?, expiration_date?, notes? }
 */
export const createPurchase = async (purchaseData, items = []) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const insertPayload = {
      supplier_id: purchaseData.supplier_id || null,
      // L10 — paridad con `create_purchase` del carril MCP: el encabezado
      // declara para qué proyecto es la compra, y es el default de las líneas.
      project_id: purchaseData.project_id || null,
      invoice_number: purchaseData.invoice_number || null,
      invoice_date: purchaseData.invoice_date,
      received_date: purchaseData.received_date || null,
      // Flow A: la compra nace facturada — hay invoice_number/invoice_date y
      // el gasto ya ocurrió. Idéntico al carril MCP
      // (mcp-server/src/tools/purchaseTools.js, "status: 'invoiced'"). El
      // default anterior era 'pending', valor que el CHECK de la tabla
      // rechaza: toda compra creada desde la web fallaba.
      status: purchaseData.status || PURCHASE_STATUS.INVOICED,
      tax_amount: purchaseData.tax_amount || 0,
      currency: purchaseData.currency || 'CRC',
      exchange_rate: purchaseData.exchange_rate || 1,
      attachment_url: purchaseData.attachment_url || null,
      notes: purchaseData.notes || null,
      location: purchaseData.location || null,
      created_by: user.id,
    };

    const { data: purchase, error: purchaseError } = await supabase
      .from('purchases')
      .insert(insertPayload)
      .select()
      .single();

    if (purchaseError) throw purchaseError;

    if (items.length > 0) {
      const itemsPayload = items.map((item) => ({
        purchase_id: purchase.id,
        inventory_id: item.inventory_id,
        quantity_ordered: item.quantity_ordered,
        unit_cost: item.unit_cost,
        lot_number: item.lot_number || null,
        expiration_date: item.expiration_date || null,
        notes: item.notes || null,
        // L10 — proyecto efectivo (línea > encabezado). La herencia que el
        // COMMENT de la columna declara NO existe en la base: si no se escribe
        // acá, la línea queda invisible para todo cálculo de costo por proyecto.
        project_id: effectiveLineProjectId(item, purchaseData),
      }));

      const { error: itemsError } = await supabase
        .from('purchase_items')
        .insert(itemsPayload);

      if (itemsError) throw itemsError;
    }

    return { data: purchase, error: null };
  } catch (error) {
    console.error('Create purchase error:', error);
    return { data: null, error };
  }
};

/**
 * Escritor privado del header. NO valida `status` — es el único punto
 * por el que las transiciones legítimas de este carril escriben.
 */
const writePurchaseHeader = async (purchaseId, payload) => {
  const { data, error } = await supabase
    .from('purchases')
    .update({ ...payload, updated_at: new Date().toISOString() })
    .eq('id', purchaseId)
    .select()
    .single();
  if (error) throw error;
  return data;
};

/**
 * Update a purchase header. Items are managed separately.
 *
 * `status` NO se acepta acá — paridad de carriles con
 * `mcp-server/src/tools/purchaseTools.js:353-357`, que ya lo rechaza.
 *
 * Antes sí estaba en la lista `allowed`, y eso permitía saltar de 'draft'
 * a 'approved' (o directo a 'invoiced') desde la web sin pasar por
 * 'pending_approval' y sin que nadie aprobara nada. La regla existía solo
 * en el carril MCP; el carril que usa Jorge todos los días no la tenía.
 * Es el patrón de #353 otra vez.
 *
 * La defensa de verdad es el trigger `trg_enforce_purchase_approval`
 * (migración 20260728120000), que aplica venga la escritura de donde
 * venga. Esto es la capa de arriba, para dar un error entendible.
 */
export const updatePurchase = async (purchaseId, updates) => {
  try {
    if (updates && Object.prototype.hasOwnProperty.call(updates, 'status')) {
      throw new Error(
        'El estado de una compra no se edita a mano. Usá el flujo de aprobación (aprobar / rechazar / recibir / adjuntar factura).'
      );
    }

    const allowed = [
      'supplier_id',
      // L10 — corregir a qué proyecto pertenece una compra es metadata, no una
      // transición de estado. Sin esto el usuario cambia el select "Proyecto"
      // en la edición, ve "Compra actualizada" y no cambió nada.
      'project_id',
      'invoice_number',
      'invoice_date',
      'received_date',
      'tax_amount',
      'currency',
      'exchange_rate',
      'attachment_url',
      'notes',
      'location',
    ];
    const payload = Object.fromEntries(
      Object.entries(updates).filter(([k]) => allowed.includes(k))
    );

    return { data: await writePurchaseHeader(purchaseId, payload), error: null };
  } catch (error) {
    console.error('Update purchase error:', error);
    return { data: null, error };
  }
};

/**
 * Cancel a purchase. The trigger keeps totals in sync.
 * Transición explícita, no una edición de campo — por eso no pasa por
 * `updatePurchase`.
 */
export const cancelPurchase = async (purchaseId) => {
  try {
    return {
      data: await writePurchaseHeader(purchaseId, { status: PURCHASE_STATUS.CANCELLED }),
      error: null
    };
  } catch (error) {
    console.error('Cancel purchase error:', error);
    return { data: null, error };
  }
};

/**
 * Delete a purchase (only allowed when pending and no batches generated).
 */
export const deletePurchase = async (purchaseId) => {
  try {
    const { error } = await supabase
      .from('purchases')
      .delete()
      .eq('id', purchaseId);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Delete purchase error:', error);
    return { error };
  }
};

// =====================================================
// Line item operations
// =====================================================

export const addPurchaseItem = async (purchaseId, item) => {
  try {
    const payload = {
      purchase_id: purchaseId,
      inventory_id: item.inventory_id,
      quantity_ordered: item.quantity_ordered,
      unit_cost: item.unit_cost,
      lot_number: item.lot_number || null,
      expiration_date: item.expiration_date || null,
      notes: item.notes || null,
      // L10 — una línea agregada DESPUÉS también tiene que quedar tagueada.
      // Acá no hay encabezado a mano (sólo el id), así que el caller manda el
      // proyecto explícito; si no lo manda queda NULL y la lectura la rescata
      // por herencia desde `purchases.project_id`.
      project_id: item.project_id || null,
    };

    const { data, error } = await supabase
      .from('purchase_items')
      .insert(payload)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Add purchase item error:', error);
    return { data: null, error };
  }
};

export const updatePurchaseItem = async (itemId, updates) => {
  try {
    const allowed = [
      'inventory_id',
      'quantity_ordered',
      'unit_cost',
      'lot_number',
      'expiration_date',
      'notes',
      'status',
      // L10 — re-tagear una línea mal asignada tiene que ser posible desde la
      // app; si no, la única reparación posible es SQL a mano contra prod.
      'project_id',
    ];
    const payload = Object.fromEntries(
      Object.entries(updates).filter(([k]) => allowed.includes(k))
    );

    const { data, error } = await supabase
      .from('purchase_items')
      .update({ ...payload, updated_at: new Date().toISOString() })
      .eq('id', itemId)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Update purchase item error:', error);
    return { data: null, error };
  }
};

export const deletePurchaseItem = async (itemId) => {
  try {
    const { error } = await supabase
      .from('purchase_items')
      .delete()
      .eq('id', itemId);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Delete purchase item error:', error);
    return { error };
  }
};

/**
 * #626 — el texto que ve Jorge cuando la línea no está ligada al catálogo.
 *
 * PARIDAD DE CARRIL. El carril MCP (`mcp-server/src/tools/purchaseOrderTools.js`
 * y `parityTools.js`) lleva esta misma precondición; acá no hay código HTTP que
 * corregir —el browser habla con PostgREST directo, no hay despachador nuestro
 * en el medio— pero el defecto de MENSAJE es idéntico: sin esto, el modal de
 * recepción mostraba
 *
 *     null value in column "inventory_id" of relation "inventory_batches"
 *     violates not-null constraint
 *
 * que no le dice a nadie qué hacer. Arreglar sólo el carril del agente habría
 * dejado roto justamente el que Jorge usa todos los días.
 */
export const LINEA_SIN_CATALOGO_MSG = (descripcion) =>
  `La línea "${descripcion}" no está ligada a un ítem del catálogo de inventario, así que no se `
  + 'puede recibir a bodega: el lote necesita saber de qué ítem es. Editá la línea y elegile un '
  + 'ítem del catálogo (o creá el ítem primero) y volvé a recibir. Si es un consumible de una sola '
  + 'vez que no lleva stock, no hace falta recibirlo.';

/**
 * Receive a single line item. The DB function generates the batch and
 * advances the purchase status as needed.
 */
export const receivePurchaseItem = async (
  itemId,
  quantity,
  options = {}
) => {
  try {
    // #626 — ver LINEA_SIN_CATALOGO_MSG. Se lee la línea antes de llamar al RPC
    // porque el RPC no puede contestar esto: revienta contra el NOT NULL.
    const { data: linea, error: lineaError } = await supabase
      .from('purchase_items')
      .select('id, description, inventory_id')
      .eq('id', itemId)
      .maybeSingle();
    if (lineaError) throw lineaError;
    if (linea && !linea.inventory_id) {
      throw new Error(LINEA_SIN_CATALOGO_MSG(linea.description || itemId));
    }

    const { data, error } = await supabase.rpc('receive_purchase_item', {
      p_item_id: itemId,
      p_quantity: quantity,
      p_lot_number: options.lot_number || null,
      p_expiration_date: options.expiration_date || null,
      p_notes: options.notes || null,
    });

    if (error) throw error;

    const result = Array.isArray(data) ? data[0] : data;
    if (result && !result.success) {
      throw new Error(result.message || 'Failed to receive item');
    }

    return { data: result, error: null };
  } catch (error) {
    console.error('Receive purchase item error:', error);
    return { data: null, error };
  }
};

/**
 * Receive every pending item in a purchase using its ordered quantity.
 */
export const receiveFullPurchase = async (purchaseId) => {
  try {
    const { data: items, error: itemsError } = await supabase
      .from('purchase_items')
      .select('*')
      .eq('purchase_id', purchaseId)
      .in('status', ['pending', 'partial']);

    if (itemsError) throw itemsError;

    const results = [];
    for (const item of items || []) {
      const remaining = Number(item.quantity_ordered) - Number(item.quantity_received);
      if (remaining > 0) {
        const result = await receivePurchaseItem(item.id, remaining);
        results.push({ itemId: item.id, ...result });
      }
    }

    return { data: results, error: null };
  } catch (error) {
    console.error('Receive full purchase error:', error);
    return { data: null, error };
  }
};

// =====================================================
// Invoice attachment upload
// =====================================================

/**
 * Upload an invoice file to Storage and return its signed URL.
 */
export const uploadInvoice = async (file, purchaseId) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const ext = file.name.split('.').pop();
    const path = `purchases/${user.id}/${purchaseId || 'new'}-${Date.now()}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from(PURCHASE_STORAGE_BUCKET)
      .upload(path, file, { upsert: false });

    if (uploadError) throw uploadError;

    // `url` es SOLO para la vista previa inmediata. Lo que se persiste en
    // `purchases.attachment_url` es `path` — ver la nota de resolveAttachmentUrl.
    const { data: signed } = await supabase.storage
      .from(PURCHASE_STORAGE_BUCKET)
      .createSignedUrl(path, RECEIPT_SIGN_TTL_SECONDS);

    return { data: { path, previewUrl: signed?.signedUrl || null }, error: null };
  } catch (error) {
    console.error('Upload invoice error:', error);
    return { data: null, error };
  }
};

const purchaseService = {
  PURCHASE_STATUS,
  PURCHASE_STATUS_LABELS,
  PURCHASE_STATUS_VARIANTS,
  EDITABLE_PURCHASE_STATUSES,
  isPurchaseEditable,
  listPurchases,
  getPurchase,
  createPurchase,
  updatePurchase,
  cancelPurchase,
  deletePurchase,
  addPurchaseItem,
  updatePurchaseItem,
  deletePurchaseItem,
  receivePurchaseItem,
  receiveFullPurchase,
  uploadInvoice,
  resolveAttachmentUrl,
  extractStoragePath,
  RECEIPT_SIGN_TTL_SECONDS,
};

export default purchaseService;
